import type {
  GraphMember,
  TreeRelationshipWorkflow,
} from '@/contexts/FamilyGraphContext';

import type {
  RelationshipHistoryEntry,
  SpousePartnerEdge,
} from '@/lib/familyUnits';

import type {
  MandatoryTreeMember,
} from './MandatoryTreeMutationAuthority';

export type CanonicalGatewayEvidenceAvailability =
  | 'available'
  | 'omitted'
  | 'not-applicable';

export interface CanonicalTreeMutationGatewayEvidenceInput {
  readonly members:
    readonly GraphMember[];

  readonly spousePartnerEdges?:
    readonly SpousePartnerEdge[];

  readonly relationshipHistory?:
    readonly RelationshipHistoryEntry[];
}

export interface CanonicalTreeMutationGatewayEvidenceContract {
  readonly members:
    readonly GraphMember[];

  readonly spousePartnerEdges:
    readonly SpousePartnerEdge[];

  readonly relationshipHistory:
    readonly RelationshipHistoryEntry[];

  readonly availability:
    Readonly<{
      members:
        CanonicalGatewayEvidenceAvailability;

      relationshipWorkflow:
        CanonicalGatewayEvidenceAvailability;

      branchOwnerMemberId:
        CanonicalGatewayEvidenceAvailability;

      spousePartnerEdges:
        CanonicalGatewayEvidenceAvailability;

      relationshipHistory:
        CanonicalGatewayEvidenceAvailability;
    }>;
}

const cloneWorkflow = (
  workflow:
    TreeRelationshipWorkflow | null | undefined,
): TreeRelationshipWorkflow | null =>
  workflow
    ? Object.freeze({
        ...workflow,
      })
    : null;

const cloneMember = (
  member:
    GraphMember,
): GraphMember =>
  Object.freeze({
    ...member,

    parentIds:
      Object.freeze([
        ...(member.parentIds ?? []),
      ]) as unknown as string[],

    relationshipWorkflow:
      cloneWorkflow(
        member.relationshipWorkflow,
      ),
  });

const cloneSpousePartnerEdge = (
  edge:
    SpousePartnerEdge,
): SpousePartnerEdge =>
  Object.freeze({
    ...edge,

    metadata:
      Object.freeze({
        ...edge.metadata,
      }),
  });

const cloneRelationshipHistory = (
  entry:
    RelationshipHistoryEntry,
): RelationshipHistoryEntry =>
  Object.freeze({
    ...entry,

    metadata:
      Object.freeze({
        ...entry.metadata,
      }),
  });

const containsWorkflowEvidence = (
  members:
    readonly GraphMember[],
): boolean =>
  members.some(
    member =>
      member.relationshipWorkflow !==
      undefined,
  );

const containsBranchOwnerEvidence = (
  members:
    readonly GraphMember[],
): boolean =>
  members.some(
    member =>
      member.branchOwnerMemberId !==
      undefined,
  );

export const createCanonicalTreeMutationGatewayEvidenceContract = (
  input:
    CanonicalTreeMutationGatewayEvidenceInput,
): CanonicalTreeMutationGatewayEvidenceContract => {
  const members =
    Object.freeze(
      input.members.map(
        cloneMember,
      ),
    );

  const spousePartnerEdges =
    Object.freeze(
      (input.spousePartnerEdges ?? [])
        .map(
          cloneSpousePartnerEdge,
        ),
    );

  const relationshipHistory =
    Object.freeze(
      (input.relationshipHistory ?? [])
        .map(
          cloneRelationshipHistory,
        ),
    );

  return Object.freeze({
    members,

    spousePartnerEdges,

    relationshipHistory,

    availability:
      Object.freeze({
        members:
          'available',

        relationshipWorkflow:
          containsWorkflowEvidence(
            input.members,
          )
            ? 'available'
            : 'omitted',

        branchOwnerMemberId:
          containsBranchOwnerEvidence(
            input.members,
          )
            ? 'available'
            : 'omitted',

        spousePartnerEdges:
          input.spousePartnerEdges ===
            undefined
            ? 'omitted'
            : 'available',

        relationshipHistory:
          input.relationshipHistory ===
            undefined
            ? 'omitted'
            : 'available',
      }),
  });
};

export interface CanonicalTreeMutationAfterGraphEvidenceInput {
  readonly beforeMembers:
    readonly GraphMember[];

  readonly afterMembers:
    readonly MandatoryTreeMember[];
}

export const rebuildCanonicalTreeMutationAfterGraphEvidence = (
  input:
    CanonicalTreeMutationAfterGraphEvidenceInput,
): readonly GraphMember[] => {
  const beforeById =
    new Map(
      input.beforeMembers.map(
        member =>
          [member.id, member] as const,
      ),
    );

  return Object.freeze(
    input.afterMembers.map(
      member => {
        const existing =
          beforeById.get(
            member.id,
          );

        return cloneMember({
          id:
            member.id,

          ownerUserId:
            existing?.ownerUserId ??
            'unknown-owner',

          name:
            existing?.name ??
            member.id,

          nickname:
            existing?.nickname ?? null,

          treeNamePreference:
            existing?.treeNamePreference,

          relationship:
            existing?.relationship ??
            'unknown',

          side:
            existing?.side ??
            'unknown',

          photoUrl:
            existing?.photoUrl ?? null,

          birthYear:
            existing?.birthYear ?? null,

          birthDate:
            existing?.birthDate ?? null,

          birthMonth:
            existing?.birthMonth ?? null,

          birthDay:
            existing?.birthDay ?? null,

          birthdayVisibility:
            existing?.birthdayVisibility ??
            'family',

          birthdayReminders:
            existing?.birthdayReminders ??
            true,

          livingStatus:
            existing?.livingStatus ?? null,

          notes:
            existing?.notes ?? null,

          city:
            existing?.city ?? null,

          description:
            existing?.description ?? null,

          approvalStatus:
            member.approvalStatus ??
            existing?.approvalStatus ??
            'approved',

          confirmationsCount:
            existing?.confirmationsCount ??
            0,

          createdBy:
            existing?.createdBy ?? null,

          createdAt:
            existing?.createdAt ??
            '1970-01-01T00:00:00.000Z',

          parentIds:
            [...(member.parentIds ?? [])],

          spouseId:
            member.spouseId ?? null,

          profileState:
            member.profileState ??
            existing?.profileState ??
            'active',

          claimedByUserId:
            member.claimedByUserId ??
            null,

          claimedAt:
            existing?.claimedAt ?? null,

          authEmail:
            existing?.authEmail ?? null,

          relationshipOverride:
            existing
              ?.relationshipOverride ??
            null,

          branchOwnerMemberId:
            existing
              ?.branchOwnerMemberId ??
            null,

          relationshipWorkflow:
            cloneWorkflow(
              existing
                ?.relationshipWorkflow,
            ),
        });
      },
    ),
  );
};
